/**
 * Wave 6 S3 -- IdleWarningModal.
 *
 * Shows a countdown when the user is about to be auto-signed-out. Two
 * actions: "Stay signed in" (resets the timer) and "Sign out now" (force
 * logout). Pure presentation; the wrapping IdleTimerProvider is what
 * actually invokes the auth store / API.
 */
import { useTranslation } from 'react-i18next'
import { ShieldAlert } from 'lucide-react'

interface IdleWarningModalProps {
  isOpen: boolean
  msUntilIdle: number
  onStay: () => void
  onLogout: () => void
}

function fmt(ms: number) {
  const s = Math.max(0, Math.ceil(ms / 1000))
  const mm = Math.floor(s / 60)
    .toString()
    .padStart(2, '0')
  const ss = (s % 60).toString().padStart(2, '0')
  return `${mm}:${ss}`
}

export default function IdleWarningModal(props: IdleWarningModalProps) {
  const { isOpen, msUntilIdle, onStay, onLogout } = props
  const { t } = useTranslation()
  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40"
      role="dialog"
      aria-labelledby="idle-warning-title"
      aria-modal="true"
      data-testid="idle-warning-modal"
    >
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center gap-3">
          <ShieldAlert className="h-6 w-6 text-orange-500" />
          <h2 id="idle-warning-title" className="text-lg font-semibold">
            {t('session.idleTitle', { defaultValue: "You're about to be signed out" })}
          </h2>
        </div>
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
          {t('session.idleBody', {
            defaultValue:
              "We didn't detect any activity. For your security, you'll be signed out automatically.",
          })}
        </p>
        <div
          className="mt-4 text-3xl font-mono text-center text-orange-600"
          data-testid="idle-countdown"
          aria-live="polite"
        >
          {fmt(msUntilIdle)}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onLogout}
            className="px-4 py-2 border rounded"
            data-testid="idle-logout-now"
          >
            {t('session.logoutNow', { defaultValue: 'Sign out now' })}
          </button>
          <button
            type="button"
            onClick={onStay}
            className="px-4 py-2 bg-blue-600 text-white rounded"
            data-testid="idle-stay-signed-in"
          >
            {t('session.staySignedIn', { defaultValue: 'Stay signed in' })}
          </button>
        </div>
      </div>
    </div>
  )
}
